import { ReactNode, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { VersionContext } from "./VersionContext";
import { Signal, useSignalCleanup } from "@/utils/signal";

/**
 * Holds the currently-selected version for a workspace/dataset scaffold. The version comes from the
 * `/versions/:version` path param, and the last explicit selection is persisted so tabs that carry
 * no version (Discussions/Settings) keep showing it and the Overview/Dataset/Files tabs return to
 * it. Mount with `key={entity.id}` so persistence resets when switching to a different entity.
 */
export function VersionProvider({
  latestVersion,
  children,
}: {
  latestVersion: string | null;
  children: ReactNode;
}) {
  const { version } = useParams();
  const live = version ?? null;
  const [persisted, setPersisted] = useState<string | null>(live);

  useEffect(() => {
    if (live != null) setPersisted(live);
  }, [live]);

  const openPanel = useMemo(() => new Signal(false), []);
  useSignalCleanup(openPanel);

  // Close the panel whenever the selected version changes — e.g. picking a
  // version from the panel navigates to it, which should dismiss the panel.
  useEffect(() => {
    openPanel.dispatch(false);
  }, [version, openPanel]);

  const selectedVersion = live ?? persisted;
  const value = useMemo(
    () => ({
      selectedVersion,
      currentVersion: selectedVersion ?? latestVersion,
      openPanel,
    }),
    [selectedVersion, latestVersion, openPanel],
  );

  return (
    <VersionContext.Provider value={value}>{children}</VersionContext.Provider>
  );
}
